export type AuthRole = 'admin' | 'hr' | 'manager' | 'jr_ae' | 'employee';

export const AUTH_ROLE_LABELS: Record<AuthRole, string> = {
  admin: 'Admin',
  hr: 'HR',
  manager: 'Manager',
  // Stored as jr_ae; people know the role as Jr manager.
  jr_ae: 'Jr manager',
  employee: 'Employee',
};

export const MANAGER_LIKE_ROLES = ['manager', 'jr_ae'] as const;

export type ManagerLikeRole = (typeof MANAGER_LIKE_ROLES)[number];

export const isManagerLike = (
  role: string | null | undefined
): role is ManagerLikeRole =>
  role === 'manager' || role === 'jr_ae';

export const formatAuthRole = (role: AuthRole | null | undefined) =>
  role ? AUTH_ROLE_LABELS[role] : 'User';

/**
 * The employee role (and any role the app doesn't know) reads only its own
 * records. Of everyone else the API gives them the directory: name, title,
 * team, manager and department, never WFH setup, balances or HR status.
 * Mirrors readsOnlyOwnRecords in backend/src/services/employeeAccessService.ts.
 */
export const readsOnlyOwnRecords = (role: string | null | undefined) => {
  const current = role || 'employee';
  return current !== 'admin' && current !== 'hr' && current !== 'manager' && current !== 'jr_ae';
};

interface ScopeableEmployee {
  managerUserId?: string | null;
  managerName?: string | null;
  jrAeUserId?: string | null;
  department?: {
    _id?: string;
    id?: string;
  } | null;
}

interface EmployeeScopeContext {
  role: string | null | undefined;
  userId: string | null | undefined;
  managerName?: string | null;
  managerDepartmentIds?: readonly string[] | ReadonlySet<string>;
}

const normalizeName = (value: string | null | undefined) =>
  value?.trim().toLowerCase() || '';

const includesDepartment = (
  departmentIds: EmployeeScopeContext['managerDepartmentIds'],
  departmentId: string | undefined
) => {
  if (!departmentIds || !departmentId) return false;
  return Array.from(departmentIds).includes(departmentId);
};

export const scopesEmployee = (
  employee: ScopeableEmployee,
  context: EmployeeScopeContext
) => {
  const { role, userId } = context;
  if (!userId) return false;

  if (role === 'jr_ae') {
    return employee.jrAeUserId === userId;
  }

  if (role !== 'manager') {
    return false;
  }

  if (employee.managerUserId) {
    return employee.managerUserId === userId;
  }

  const employeeManagerName = normalizeName(employee.managerName);
  const currentManagerName = normalizeName(context.managerName);
  if (employeeManagerName && currentManagerName && employeeManagerName === currentManagerName) {
    return true;
  }

  const departmentId = employee.department?._id || employee.department?.id;
  return includesDepartment(context.managerDepartmentIds, departmentId);
};

/** Why approve, decline and edit are off for someone's own time-off request. */
export const OWN_REQUEST_LOCKED_REASON = 'This is your own request. Your manager or HR approves or changes it.';

/**
 * True when the viewer may not approve, decline or edit this request because
 * it is their own. Only admin and HR change their own time off; the API
 * refuses everyone else (timeOffChangeRefusal in the backend).
 */
export const isOwnRequestLocked = (
  employee: { userId?: string | null } | null | undefined,
  { role, userId }: { role: string | null | undefined; userId: string | null | undefined }
) => role !== 'admin' && role !== 'hr' && Boolean(userId && employee?.userId === userId);
